import React, { useCallback } from 'react';

import { VideoView, useVideoPlayer } from 'expo-video';
import { useAppTakeover } from 'features/app-takeover/hooks/use-app-takeover';
import { takeoverAudioMuted } from 'features/app-takeover/utils/play-audio-once';
import { View as NativeView } from 'react-native';

import { IServerOrder } from '@rbi-ctg/menu';
import { FullWidthAutoHeightImage } from 'components/picture/full-width-auto-height-image';
import { Box } from 'components/ui/box';
import useOrderNumber from 'hooks/use-order-number';
import { useAuthContext } from 'state/auth';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { spacing } from 'styles/constants';
import { isWeb, region } from 'utils/environment';
import { Region } from 'utils/environment/types';
import { TraceName, stopTrace } from 'utils/performance/trace';

import { Container, Header, Instructions, OrderNumber } from './styled';
import { getOrderConfirmationHeader, getOrderConfirmationInstructions } from './utils';

interface IOrderConfirmationHeader {
  serverOrder: IServerOrder;
}
const OrderConfirmationHeader = ({ serverOrder }: IOrderConfirmationHeader) => {
  const { user } = useAuthContext();

  const orderNumber = useOrderNumber(serverOrder);
  const isMobileOrderForDriveThruLaneEnabled = useFlag(
    LaunchDarklyFlag.ENABLE_MOBILE_ORDER_DRIVE_THRU_LANE
  );
  const formatedOrderNumber = t('orderConfirmationNumber', { orderNumber });
  const serviceMode = serverOrder.cart.serviceMode;
  const userName = (user?.details.name ?? '').split(' ')[0];

  const {
    headerVideo: takeoverVideo,
    headerImage: takeoverHeaderImage,
    headerFontColor: takeoverFontColor,
    headerBackgroundColor: takeoverBackgroundColor,
    headerVideoAspectRatio: takeoverAspectRatio,
    ConfirmHeaderOverlay: TakeoverConfirmHeaderOverlay,
  } = useAppTakeover()?.orderConfirmDetails || {};

  const headerText = getOrderConfirmationHeader({ userName });
  const instructions = getOrderConfirmationInstructions({
    userName,
    serviceMode,
    isMobileOrderForDriveThruLaneEnabled,
  });

  const placeOrderStopTrace = useCallback(() => {
    stopTrace(TraceName.PLACE_ORDER_TO_RECEIPT, { rbiOrderId: serverOrder?.rbiOrderId });
  }, [serverOrder?.rbiOrderId]);

  return (
    <Container
      style={{
        marginHorizontal: spacing.$4 * -1,
        marginTop: spacing.$4 * -1,
      }}
    >
      {TakeoverConfirmHeaderOverlay && (
        <TakeoverConfirmHeaderOverlay backgroundColor={takeoverBackgroundColor} />
      )}
      <Box
        onLayout={placeOrderStopTrace}
        style={{
          backgroundColor: takeoverBackgroundColor,
          paddingTop: spacing.$4,
          paddingHorizontal: spacing.$4,
          marginBottom: -1, // fixes mysterious 1px gap between video and view on android
        }}
      >
        {orderNumber && <OrderNumber color={takeoverFontColor}>{formatedOrderNumber}</OrderNumber>}
        {!!headerText && !!userName && (
          <NativeView
            accessible
            accessibilityLabel={headerText}
            accessibilityRole="header"
            nativeID="order-confirmation-status-message"
            testID="order-confirmation-status-message"
          >
            <Header color={takeoverFontColor}>{headerText}</Header>
          </NativeView>
        )}
        <Instructions color={takeoverFontColor}>{instructions}</Instructions>
      </Box>
      {takeoverVideo && (
        <TakeoverVideo video={takeoverVideo} aspectRatio={takeoverAspectRatio ?? 1} />
      )}
      {takeoverHeaderImage && <FullWidthAutoHeightImage source={takeoverHeaderImage} />}
    </Container>
  );
};

// separate component to avoid allocating resources for this non-standard path
const TakeoverVideo = ({ video, aspectRatio }: { video: any; aspectRatio: number }) => {
  const player = useVideoPlayer(video, player => {
    player.loop = false;
    player.audioMixingMode = 'mixWithOthers';

    if (region === Region.CA || takeoverAudioMuted.current) {
      player.muted = true;
    }

    if (isWeb) {
      // web requires a timeout due to a bug, maybe could remove in SDK 54 https://github.com/expo/expo/issues/36350
      setTimeout(() => player.play(), 0);
    } else {
      player.play();
    }
  });

  return (
    <VideoView
      style={{ width: '100%', aspectRatio }}
      contentFit="cover"
      nativeControls={false}
      allowsPictureInPicture={false}
      // @ts-ignore - VideoPlayer type mismatch between native and web
      player={player}
      playsInline
    />
  );
};

export default OrderConfirmationHeader;
